import { normalizeAnthropometryMethod } from "./antropometria.js";

const METHOD_CONFIG = Object.freeze({
  yuhasz: { mode: "two_components", label: "Dos componentes · Yuhasz", chart: "somatocarta" },
  durnin_siri: { mode: "two_components", label: "Dos componentes · Durnin + Siri", chart: "somatocarta" },
  rfm: { mode: "two_components", label: "Masa grasa relativa", chart: "somatocarta" },
  slaughter: { mode: "two_components", label: "Dos componentes pediátrico · Slaughter", chart: "somatocarta" },
  parizkova: { mode: "two_components", label: "Dos componentes pediátrico · Parízková", chart: "somatocarta" },
  brook_lohman: { mode: "two_components", label: "Dos componentes · Brook + Lohman", chart: "somatocarta" },
  durnin_rahaman_lohman: { mode: "two_components", label: "Dos componentes · Durnin & Rahaman + Lohman", chart: "somatocarta" },
  sumatoria_pliegues: { mode: "sums", label: "Sumatoria de pliegues", chart: "somatocarta" },
  cinco_componentes: { mode: "five_components", label: "Cinco componentes · Ross & Kerr", chart: "somatocarta" },
});

export const ASSESSMENT_REPORT_VERSION = "3.1.0";

export function normalizeAssessmentPopulation(value) {
  return String(value || "").toLowerCase() === "pediatric" ? "pediatric" : "adult";
}

export function normalizeAssessmentMethod(value) {
  return normalizeAnthropometryMethod(value);
}

export function selectAssessmentReportConfig({ populationProfile = "adult", method = "yuhasz", sequenceNumber = 1 } = {}) {
  const normalizedMethod = normalizeAssessmentMethod(method);
  const population = normalizeAssessmentPopulation(populationProfile);
  const config = METHOD_CONFIG[normalizedMethod];
  const sequence = Math.max(1, Number(sequenceNumber) || 1);
  return {
    id: `${population}_${config.mode}_${sequence === 1 ? "initial" : "control"}`,
    populationProfile: population,
    populationLabel: population === "pediatric" ? "Pediátrico" : "Adulto",
    method: normalizedMethod,
    methodLabel: config.label,
    mode: config.mode,
    visit: sequence === 1 ? "initial" : "control",
    visitLabel: sequence === 1 ? "Valoración inicial" : `Control ${sequence - 1}`,
    charts: [config.chart],
    formulaProfile: "formato_antropometria_2026",
    formulaVersion: ASSESSMENT_REPORT_VERSION,
    note: population === "pediatric"
      ? "Perfil pediátrico seleccionado; confirma que las referencias antropométricas del formato actualizado correspondan a la edad del paciente."
      : "Perfil adulto seleccionado; se aplican las fórmulas del formato actualizado.",
  };
}

export function buildAssessmentComparison(current, previous) {
  if (!current || !previous) return { available: false, metrics: [] };
  const currentResults = current.results || {};
  const previousResults = previous.results || {};
  const candidates = [
    ["IMC", currentResults.bmi, previousResults.bmi, "kg/m²", Number(currentResults.bmi) > 0 && Number(previousResults.bmi) > 0],
    ["Grasa corporal", currentResults.primaryComposition?.fatPercent, previousResults.primaryComposition?.fatPercent, "%", Number(currentResults.primaryComposition?.fatPercent) > 0 && Number(previousResults.primaryComposition?.fatPercent) > 0],
    ["Masa libre de grasa", currentResults.primaryComposition?.leanKg, previousResults.primaryComposition?.leanKg, "kg", Number(currentResults.primaryComposition?.leanKg) > 0 && Number(previousResults.primaryComposition?.leanKg) > 0],
    ["Masa muscular", currentResults.primaryComposition?.muscleKg, previousResults.primaryComposition?.muscleKg, "kg", Number(currentResults.primaryComposition?.muscleKg) > 0 && Number(previousResults.primaryComposition?.muscleKg) > 0],
    ["Cintura/talla", currentResults.waistToHeight, previousResults.waistToHeight, "", Number(currentResults.waistToHeight) > 0 && Number(previousResults.waistToHeight) > 0],
    [
      "Maduración PHV",
      currentResults.pediatricGrowth?.maturityOffsetYears,
      previousResults.pediatricGrowth?.maturityOffsetYears,
      "años",
      Boolean(currentResults.pediatricGrowth?.maturityComplete && previousResults.pediatricGrowth?.maturityComplete),
    ],
  ];
  return {
    available: true,
    metrics: candidates
      .filter(([, currentValue, previousValue, , valid]) => valid && Number.isFinite(Number(currentValue)) && Number.isFinite(Number(previousValue)))
      .map(([label, currentValue, previousValue, unit]) => ({
        label,
        current: Number(currentValue),
        previous: Number(previousValue),
        delta: Number(currentValue) - Number(previousValue),
        unit,
      })),
  };
}
